<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h1 class="font-weight-bolder mb-0">
            Membresías
          </h1>
          <small class="text-muted">
            Define qué incluye cada plan. Un límite vacío significa sin límite.
          </small>
        </div>
        <div>
          <b-button
            variant="outline-primary"
            class="mr-1"
            :disabled="loading"
            @click="load"
          >
            <feather-icon icon="RefreshCwIcon" />
            Actualizar
          </b-button>
          <b-button
            variant="primary"
            :disabled="loading || !dirty"
            @click="save"
          >
            Guardar cambios
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-overlay :show="loading">
      <b-row class="mb-1">
        <b-col
          v-for="plan in draft"
          :key="`header-${plan.id}`"
          md="6"
        >
          <b-card class="mb-1">
            <b-row>
              <b-col md="6">
                <b-form-group label="Nombre">
                  <b-form-input v-model="plan.name" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Precio mensual">
                  <b-form-input
                    v-model="plan.price"
                    type="number"
                    min="0"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label="Leyenda">
                  <b-form-input
                    v-model="plan.tagline"
                    placeholder="Gratis siempre"
                  />
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-checkbox
                  v-model="plan.active"
                  switch
                  inline
                >
                  Visible para el cliente
                </b-form-checkbox>
                <b-form-checkbox
                  v-model="plan.purchasable"
                  switch
                  inline
                >
                  Se puede contratar
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <b-card
        v-for="category in catalog"
        :key="category.key"
        :title="category.label"
        class="mb-1"
      >
        <b-table-simple
          responsive
          small
          class="mb-0"
        >
          <b-thead>
            <b-tr>
              <b-th style="min-width: 280px">
                Concepto
              </b-th>
              <b-th
                v-for="plan in draft"
                :key="`th-${category.key}-${plan.id}`"
                class="text-center"
                style="min-width: 160px"
              >
                {{ plan.name }}
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="feature in category.features"
              :key="feature.key"
            >
              <b-td>
                <div class="font-weight-bolder">
                  {{ feature.name }}
                </div>
                <small class="text-muted">{{ feature.description }}</small>
              </b-td>
              <b-td
                v-for="plan in draft"
                :key="`td-${feature.key}-${plan.id}`"
                class="text-center"
              >
                <b-form-checkbox
                  v-if="feature.kind === 'boolean'"
                  v-model="plan.features[feature.key]"
                  switch
                  class="d-inline-block"
                />
                <b-form-input
                  v-else
                  v-model="plan.features[feature.key]"
                  type="number"
                  min="0"
                  placeholder="Sin límite"
                  class="text-center"
                />
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BOverlay, BFormGroup, BFormInput, BFormCheckbox,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BOverlay,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },

  data() {
    return {
      draft: [],
      snapshot: '',
    }
  },

  computed: {
    ...mapGetters('memberships', {
      plans: 'membershipPlans',
      catalog: 'membershipCatalog',
      loading: 'membershipsLoading',
    }),

    dirty() {
      return JSON.stringify(this.draft) !== this.snapshot
    },
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('memberships', ['getMembershipPlans', 'updateMembershipPlan']),

    load() {
      return this.getMembershipPlans().then(() => this.resetDraft())
    },

    // El input numerico devuelve texto y el vacio es el "sin limite" del backend.
    resetDraft() {
      this.draft = this.plans.map(plan => ({
        ...plan,
        features: { ...plan.features },
      }))
      this.snapshot = JSON.stringify(this.draft)
    },

    save() {
      const pending = this.draft.map(plan => this.updateMembershipPlan(plan))
      return Promise.all(pending)
        .then(() => {
          this.resetDraft()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Membresías actualizadas',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.messages?.[0] || 'No se pudieron guardar las membresías',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>
